import type { PropsWithChildren } from 'react'
import { useMemo, useReducer } from 'react'
import {
  FlashcardsStateContext,
  initialFlashcardsState,
  type FlashcardGameSession,
  type FlashcardsFeatureState,
  type FlashcardsStateActions,
  type FlashcardsStateContextValue,
} from './flashcards-state'
import type { FlashcardCard, FlashcardCategory } from '@services/firebase/flashcards'

type FlashcardsAction =
  | { type: 'clear-selected-cards' }
  | { type: 'close-slideshow' }
  | { type: 'finish-game-session' }
  | { type: 'reset-game-session' }
  | { type: 'score-current-prompt'; payload: 'correct' | 'incorrect' }
  | { type: 'set-active-category'; payload: FlashcardCategory | null }
  | { type: 'set-cards'; payload: { cards: FlashcardCard[]; categoryId: string } }
  | { type: 'set-cards-loading'; payload: boolean }
  | { type: 'set-categories'; payload: FlashcardCategory[] }
  | { type: 'set-categories-loading'; payload: boolean }
  | { type: 'set-error-message'; payload: string | null }
  | { type: 'set-game-active-index'; payload: number }
  | { type: 'open-slideshow'; payload: number }
  | { type: 'start-game-session'; payload: FlashcardGameSession }
  | { type: 'toggle-selected-card'; payload: string }

function flashcardsReducer(
  state: FlashcardsFeatureState,
  action: FlashcardsAction,
): FlashcardsFeatureState {
  switch (action.type) {
    case 'clear-selected-cards':
      return { ...state, selectedCardIds: [] }
    case 'close-slideshow':
      return {
        ...state,
        slideshow: {
          ...state.slideshow,
          open: false,
        },
      }
    case 'finish-game-session':
      return state.gameSession
        ? {
            ...state,
            gameSession: {
              ...state.gameSession,
              status: 'finished',
            },
          }
        : state
    case 'reset-game-session':
      return { ...state, gameSession: null }
    case 'score-current-prompt':
      return state.gameSession
        ? {
            ...state,
            gameSession: {
              ...state.gameSession,
              correctCount:
                action.payload === 'correct'
                  ? state.gameSession.correctCount + 1
                  : state.gameSession.correctCount,
              incorrectCount:
                action.payload === 'incorrect'
                  ? state.gameSession.incorrectCount + 1
                  : state.gameSession.incorrectCount,
            },
          }
        : state
    case 'set-active-category':
      return { ...state, activeCategory: action.payload, gameSession: null }
    case 'set-cards':
      return {
        ...state,
        cardsByCategoryId: {
          ...state.cardsByCategoryId,
          [action.payload.categoryId]: action.payload.cards,
        },
      }
    case 'set-cards-loading':
      return {
        ...state,
        loading: {
          ...state.loading,
          cards: action.payload,
        },
      }
    case 'set-categories':
      return { ...state, categories: action.payload, categoriesLoaded: true }
    case 'set-categories-loading':
      return {
        ...state,
        loading: {
          ...state.loading,
          categories: action.payload,
        },
      }
    case 'set-error-message':
      return { ...state, errorMessage: action.payload }
    case 'set-game-active-index':
      return state.gameSession
        ? {
            ...state,
            gameSession: {
              ...state.gameSession,
              activeIndex: action.payload,
            },
          }
        : state
    case 'open-slideshow':
      return {
        ...state,
        slideshow: {
          activeIndex: action.payload,
          open: true,
        },
      }
    case 'start-game-session':
      return { ...state, gameSession: action.payload }
    case 'toggle-selected-card':
      return {
        ...state,
        selectedCardIds: state.selectedCardIds.includes(action.payload)
          ? state.selectedCardIds.filter((cardId) => cardId !== action.payload)
          : [...state.selectedCardIds, action.payload],
      }
    default:
      return state
  }
}

export function FlashcardsStateProvider({ children }: PropsWithChildren) {
  const [state, dispatch] = useReducer(flashcardsReducer, initialFlashcardsState)

  const actions = useMemo<FlashcardsStateActions>(
    () => ({
      clearErrorMessage: () => {
        dispatch({ type: 'set-error-message', payload: null })
      },
      clearSelectedCards: () => {
        dispatch({ type: 'clear-selected-cards' })
      },
      closeSlideshow: () => {
        dispatch({ type: 'close-slideshow' })
      },
      finishGameSession: () => {
        dispatch({ type: 'finish-game-session' })
      },
      openSlideshow: (activeIndex = 0) => {
        dispatch({ type: 'open-slideshow', payload: activeIndex })
      },
      resetGameSession: () => {
        dispatch({ type: 'reset-game-session' })
      },
      scoreCurrentPrompt: (result) => {
        dispatch({ type: 'score-current-prompt', payload: result })
      },
      setActiveCategory: (category) => {
        dispatch({ type: 'set-active-category', payload: category })
      },
      setCards: (categoryId, cards) => {
        dispatch({ type: 'set-cards', payload: { cards, categoryId } })
      },
      setCardsLoading: (isLoading) => {
        dispatch({ type: 'set-cards-loading', payload: isLoading })
      },
      setCategories: (categories) => {
        dispatch({ type: 'set-categories', payload: categories })
      },
      setCategoriesLoading: (isLoading) => {
        dispatch({ type: 'set-categories-loading', payload: isLoading })
      },
      setErrorMessage: (message) => {
        dispatch({ type: 'set-error-message', payload: message })
      },
      setGameActiveIndex: (activeIndex) => {
        dispatch({ type: 'set-game-active-index', payload: activeIndex })
      },
      startGameSession: (session) => {
        dispatch({ type: 'start-game-session', payload: session })
      },
      toggleSelectedCard: (cardId) => {
        dispatch({ type: 'toggle-selected-card', payload: cardId })
      },
    }),
    [],
  )

  const value = useMemo<FlashcardsStateContextValue>(
    () => ({
      actions,
      state,
    }),
    [actions, state],
  )

  return (
    <FlashcardsStateContext.Provider value={value}>
      {children}
    </FlashcardsStateContext.Provider>
  )
}

